import { useCallback, useMemo, useState } from 'react'
import type { Aggregates, AreaId, PulseResponse } from '../types/pulse'
import { seedAggregates } from '../data/pulse'
import { matchTheme } from '../utils/pulse'

const emptyResponse: PulseResponse = {
  ranking: [],
  topArea: null,
  followUpChoice: null,
  followUpFreeText: '',
  teamRating: null,
  techRating: null,
  openResponse: '',
  submittedAt: null,
}

function cloneSeed(): Aggregates {
  return {
    rankFirst: { ...seedAggregates.rankFirst },
    followUp: {
      diagnosis: { ...seedAggregates.followUp.diagnosis },
      treatment: { ...seedAggregates.followUp.treatment },
      followup: { ...seedAggregates.followUp.followup },
    },
    team: { ...seedAggregates.team },
    tech: { ...seedAggregates.tech },
    openResponses: seedAggregates.openResponses,
    themes: seedAggregates.themes.map((t) => ({ ...t })),
  }
}

/**
 * Folds the delegate's own answers into the congress totals, so every result
 * screen reports the room *including* this response. Derived rather than
 * incremented, so revisiting a question never double-counts it.
 */
function derive(response: PulseResponse): Aggregates {
  const agg = cloneSeed()

  if (response.topArea) {
    agg.rankFirst[response.topArea] += 1
    if (response.followUpChoice) {
      const counts = agg.followUp[response.topArea]
      counts[response.followUpChoice] = (counts[response.followUpChoice] ?? 0) + 1
    }
  }

  for (const key of ['team', 'tech'] as const) {
    const value = key === 'team' ? response.teamRating : response.techRating
    if (value !== null) {
      agg[key] = {
        n: agg[key].n + 1,
        sum: agg[key].sum + value,
        high: agg[key].high + (value >= 4 ? 1 : 0),
      }
    }
  }

  const open = response.openResponse.trim()
  if (open) {
    agg.openResponses += 1
    const themeId = matchTheme(agg.themes, open)
    if (themeId) {
      agg.themes = agg.themes.map((t) => (t.id === themeId ? { ...t, count: t.count + 1 } : t))
    }
  }

  return agg
}

export interface PulseStore {
  response: PulseResponse
  aggregates: Aggregates
  submitRanking: (order: AreaId[]) => void
  submitFollowUp: (choice: string, freeText: string) => void
  submitRating: (key: 'team' | 'tech', value: number) => void
  submitOpen: (text: string) => void
  reset: () => void
}

export function usePulse(): PulseStore {
  const [response, setResponse] = useState<PulseResponse>(emptyResponse)
  const aggregates = useMemo(() => derive(response), [response])

  const stamp = (prev: PulseResponse) => prev.submittedAt ?? new Date().toISOString()

  const submitRanking = useCallback((order: AreaId[]) => {
    setResponse((prev) => {
      const top = order[0] ?? null
      const areaChanged = top !== prev.topArea
      return {
        ...prev,
        ranking: order,
        topArea: top,
        followUpChoice: areaChanged ? null : prev.followUpChoice,
        followUpFreeText: areaChanged ? '' : prev.followUpFreeText,
        submittedAt: stamp(prev),
      }
    })
  }, [])

  const submitFollowUp = useCallback((choice: string, freeText: string) => {
    setResponse((prev) => ({ ...prev, followUpChoice: choice, followUpFreeText: freeText }))
  }, [])

  const submitRating = useCallback((key: 'team' | 'tech', value: number) => {
    setResponse((prev) => ({
      ...prev,
      [key === 'team' ? 'teamRating' : 'techRating']: value,
    }))
  }, [])

  const submitOpen = useCallback((text: string) => {
    setResponse((prev) => ({ ...prev, openResponse: text }))
  }, [])

  const reset = useCallback(() => setResponse(emptyResponse), [])

  return { response, aggregates, submitRanking, submitFollowUp, submitRating, submitOpen, reset }
}
